/**
 * @file usePushNotificationNavigation.js
 * @description Opens the destination of a tapped push notification when the app was already
 * running. The service worker stores the destination; this hook reads it on a worker message
 * and whenever the app becomes visible, because iOS can drop messages to a suspended page.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES, ROUTE_BUILDERS } from '../constants/routes';
import { useAuthContext } from '../store/Contexts';
import { useIndividualViewNavigation } from './useIndividualViewNavigation';

// Shared with public/firebase-messaging-sw.js.
const PENDING_DESTINATION_CACHE = 'push-navigation';
const PENDING_DESTINATION_KEY = '/__pending-push-destination';
const PENDING_DESTINATION_TTL_MS = 5 * 60 * 1000;
// Covers iOS reloading the window at start_url right after the page already routed.
const CONSUMED_DESTINATION_RECOVERY_MS = 15 * 1000;

const PORTAL_PAGE_PATH_PATTERN = /^\/portal-pages\/(\d+)$/;
const PORTAL_BACK_STACK_KEY = 'navigationStack:back';
const PORTAL_FORWARD_STACK_KEY = 'navigationStack:forward';
const PORTAL_NAMES_KEY = 'navigationStack:names';

function isSafeInternalPath(path) {
    return (
        typeof path === 'string' &&
        path.startsWith('/') &&
        !path.startsWith('//')
    );
}

async function readPendingDestination(allowRecentlyConsumed) {
    if (!('caches' in window)) {
        return null;
    }

    try {
        const cache = await caches.open(PENDING_DESTINATION_CACHE);
        const response = await cache.match(PENDING_DESTINATION_KEY);
        if (!response) {
            return null;
        }

        const { path, savedAt, consumedAt } = await response.json();
        const isFresh =
            Number.isFinite(savedAt) &&
            Date.now() - savedAt < PENDING_DESTINATION_TTL_MS;
        const isRecoverable =
            !consumedAt ||
            (allowRecentlyConsumed &&
                Date.now() - consumedAt < CONSUMED_DESTINATION_RECOVERY_MS);
        if (isFresh && isRecoverable && isSafeInternalPath(path)) {
            return path;
        }

        return null;
    } catch (error) {
        console.warn('Unable to read the push notification destination', error);
        return null;
    }
}

async function markDestinationConsumed(expectedPath) {
    try {
        const cache = await caches.open(PENDING_DESTINATION_CACHE);
        const response = await cache.match(PENDING_DESTINATION_KEY);
        if (!response) {
            return;
        }

        const current = await response.json();
        if (current.path !== expectedPath || current.consumedAt) {
            return;
        }

        await cache.put(
            PENDING_DESTINATION_KEY,
            new Response(
                JSON.stringify({ ...current, consumedAt: Date.now() }),
                { headers: { 'Content-Type': 'application/json' } },
            ),
        );
    } catch (error) {
        console.warn(
            'Unable to mark the push notification destination as consumed',
            error,
        );
    }
}

function toLocationParts(destination) {
    const { pathname, search, hash } = new URL(
        destination,
        window.location.origin,
    );

    return { pathname, search, hash };
}

// PortalPageView only renders when router state carries a navigation stack.
function buildPortalNavigationState(pathname) {
    const portalMatch = pathname.match(PORTAL_PAGE_PATH_PATTERN);
    if (!portalMatch) {
        return undefined;
    }

    const navigationStack = [Number(portalMatch[1])];
    try {
        sessionStorage.setItem(
            PORTAL_BACK_STACK_KEY,
            JSON.stringify(navigationStack),
        );
        sessionStorage.removeItem(PORTAL_FORWARD_STACK_KEY);
        sessionStorage.removeItem(PORTAL_NAMES_KEY);
    } catch (error) {
        console.warn('Unable to store the portal navigation stack', error);
    }

    return { navigationStack };
}

function routeToDestination(
    destination,
    { navigate, location, isLoggedIn, forceIndividualView },
) {
    const target = toLocationParts(destination);

    // The destination stays stored until login completes, so portal pages still get their state.
    if (!isLoggedIn) {
        const loginPath =
            location.pathname === ROUTES.LOGIN
                ? `${location.pathname}${location.search}`
                : ROUTE_BUILDERS.loginWithCompanyCode(
                      localStorage.getItem('companyCode'),
                  );
        navigate(loginPath, {
            replace: true,
            state: { ...location.state, from: target },
        });
        return false;
    }

    forceIndividualView();
    navigate(`${target.pathname}${target.search}${target.hash}`, {
        replace: location.pathname === target.pathname,
        state: buildPortalNavigationState(target.pathname),
    });
    return true;
}

function replyToPushNavigation(pending, status) {
    const port = pending?.replyPort;
    if (!port) return;

    pending.replyPort = null;

    try {
        port.postMessage({ status });
    } catch (error) {
        console.warn('Failed to reply to push navigation', error);
    } finally {
        port.close();
    }
}

function usePushNotificationNavigation() {
    const navigate = useNavigate();
    const location = useLocation();
    const { isLoggedIn } = useAuthContext();
    const { forceIndividualView } = useIndividualViewNavigation();

    // Listeners are registered once, so they read the latest router and auth state through a ref.
    const routingContextRef = useRef(null);
    routingContextRef.current = {
        navigate,
        location,
        isLoggedIn,
        forceIndividualView,
    };
    const isConsumingRef = useRef(false);
    const rerunRequestedRef = useRef(false);
    const pendingMessageRef = useRef(null);
    const hasRoutedThisPageRef = useRef(false);

    const openPendingDestination = useCallback(
        async (messagePath, replyPort) => {
            if (isSafeInternalPath(messagePath)) {
                replyToPushNavigation(pendingMessageRef.current, 'superseeded');
                pendingMessageRef.current = { path: messagePath, replyPort };
            }

            rerunRequestedRef.current = true;

            if (isConsumingRef.current) {
                return;
            }
            isConsumingRef.current = true;
            try {
                do {
                    rerunRequestedRef.current = false;
                    const cachedDestination = await readPendingDestination(
                        !hasRoutedThisPageRef.current,
                    );

                    const directPending = pendingMessageRef.current;
                    const destination =
                        directPending?.path || cachedDestination;

                    if (!destination) {
                        continue;
                    }

                    const hasNavigated = routeToDestination(
                        destination,
                        routingContextRef.current,
                    );
                    replyToPushNavigation(
                        directPending,
                        hasNavigated ? 'handled' : 'defered',
                    );
                    if (hasNavigated) {
                        hasRoutedThisPageRef.current = true;
                        if (pendingMessageRef.current === directPending) {
                            pendingMessageRef.current = null;
                        }
                        await markDestinationConsumed(destination);
                    }
                } while (rerunRequestedRef.current);
            } catch (error) {
                console.error(
                    'Error while opening pending destination:',
                    error,
                );
            } finally {
                isConsumingRef.current = false;
            }
        },
        [],
    );

    useEffect(() => {
        if (isLoggedIn) {
            openPendingDestination();
        }
    }, [isLoggedIn, openPendingDestination]);

    useEffect(() => {
        if (!('serviceWorker' in navigator)) {
            return undefined;
        }

        const handleServiceWorkerMessage = event => {
            if (event.data?.action === 'navigate') {
                openPendingDestination(event.data.path, event.ports?.[0]);
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                openPendingDestination();
            }
        };

        navigator.serviceWorker.addEventListener(
            'message',
            handleServiceWorkerMessage,
        );
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', openPendingDestination);
        openPendingDestination();

        return () => {
            navigator.serviceWorker.removeEventListener(
                'message',
                handleServiceWorkerMessage,
            );
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange,
            );
            window.removeEventListener('focus', openPendingDestination);
        };
    }, [openPendingDestination]);
}

export { usePushNotificationNavigation };
